import api from "@/lib/api";

/**
 * Google address search, always through Toukky's own API.
 *
 * The browser never talks to Google while someone types: suggestions come
 * from GET /api/transfers/places and the address parts of a pick from
 * GET /api/places/details, both proxied by the backend with a server key.
 * The public Maps key (REACT_APP_GOOGLE_MAPS_API_KEY) stays Maps-only.
 *
 * What a pick may give a form is deliberately narrow: the Google place_id
 * (which Google allows us to keep) and the address text and parts (city,
 * postal code, country). Google's coordinates are never taken, stored or
 * shown here, so no Leaflet pin is ever moved from them; a partner still
 * places the map point by hand.
 *
 * One switch for the whole page: once the API answers that no provider is
 * configured (no key, Places disabled, daily cap reached), every address
 * field stops asking and stays plain text until the page is reloaded. A 429
 * (too many lookups from this address) pauses every field for the time the
 * API asks, a minute when it does not say.
 */

export const PLACE_KINDS = ["address", "locality"];
export const PLACE_SCOPES = ["sn", "near_sn"];
export const MIN_PLACE_QUERY = 2;

const DEFAULT_PAUSE_MS = 60 * 1000;
const MAX_PAUSE_MS = 15 * 60 * 1000;

let unavailable = false;
let pausedUntil = 0;

export const placesAvailable = (now = Date.now()) => !unavailable && now >= pausedUntil;

// For tests, and nothing else: a fresh page.
export const resetPlacesAvailability = () => { unavailable = false; pausedUntil = 0; };

export const newPlacesSession = () => (typeof crypto !== "undefined" && crypto.randomUUID
  ? crypto.randomUUID()
  : `${Date.now()}-${Math.random().toString(16).slice(2)}`);

const isTooMany = (error) => error?.response?.status === 429;

function pauseFrom(error) {
  const header = error?.response?.headers?.["retry-after"];
  const seconds = Number.parseInt(header, 10);
  const wait = Number.isFinite(seconds) && seconds > 0 ? Math.min(seconds * 1000, MAX_PAUSE_MS) : DEFAULT_PAUSE_MS;
  pausedUntil = Date.now() + wait;
}

const text = (value) => (typeof value === "string" ? value : "");

// Only the four fields the combobox shows travel on; anything else the API
// might add is dropped here, never rendered by accident.
function cleanSuggestions(rows) {
  if (!Array.isArray(rows)) return [];
  return rows
    .filter((row) => row && text(row.place_id) && text(row.label))
    .map((row) => ({ place_id: row.place_id, label: row.label, main: text(row.main), secondary: text(row.secondary) }));
}

/**
 * Suggestions for what was typed. Resolves to { available, suggestions }:
 * available is false when the page has stopped asking (no provider, a cap or
 * a 429), and the field is then plain text. A network failure rejects; the
 * caller shows no list and keeps the typed text.
 */
export async function suggestPlaces({ q, lang, kind = "address", scope = "near_sn", session }) {
  if (!placesAvailable()) return { available: false, suggestions: [] };
  try {
    const { data } = await api.get("/transfers/places", { params: { q, lang, session, kind, scope } });
    if (data?.configured === false) { unavailable = true; return { available: false, suggestions: [] }; }
    return { available: true, suggestions: cleanSuggestions(data?.suggestions) };
  } catch (error) {
    if (isTooMany(error)) { pauseFrom(error); return { available: false, suggestions: [] }; }
    throw error;
  }
}

const ADDRESS_PARTS = ["formatted_address", "locality", "sublocality", "postal_code", "country_code", "country"];

/**
 * The address parts of a picked place, under the same session token as the
 * keystrokes that found it (Google bills the session as one). Public, like
 * the suggestions: a guest booking a taxi or searching a car gets the
 * complete address too, behind the same per-address and daily caps. Resolves
 * to null when anything goes wrong (no provider, a cap or a 429, offline):
 * the picked label stays and nothing else is filled. Coordinates are never
 * part of the answer, even if one came back.
 */
export async function placeDetails({ placeId, session, lang }) {
  if (!placeId || unavailable) return null;
  try {
    const { data } = await api.get("/places/details", { params: { place_id: placeId, session, lang } });
    if (!data || data.configured === false) return null;
    const parts = { place_id: text(data.place_id) || placeId };
    for (const key of ADDRESS_PARTS) if (text(data[key]).trim()) parts[key] = data[key].trim();
    if (parts.country_code) parts.country_code = parts.country_code.toUpperCase();
    return parts;
  } catch (_) {
    return null;
  }
}

/**
 * The complete address an exact field shows after a pick: Google's
 * formatted_address (number, street, postal code, town, country), led by the
 * place's own name unless it already starts with it. "Radisson Hotel Dakar
 * Diamniadio" + "Autoroute à péage, Diamniadio 12500, Sénégal" gives both;
 * "12 Rue Carnot" + "12 Rue Carnot, Dakar 12500, Sénégal" gives the second
 * alone. Empty when there is no formatted address.
 */
export function completeAddress(main, formattedAddress) {
  const address = text(formattedAddress).trim();
  const name = text(main).trim();
  if (!address) return "";
  if (!name || address.toLowerCase().startsWith(name.toLowerCase())) return address;
  return `${name}, ${address}`;
}

const escapeRegExp = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
const oneLine = (value) => text(value).split(/\s+/).filter(Boolean).join(" ");

/**
 * Whether an address names a town as whole words, any case: "12 Rue Carnot,
 * Dakar 12500, Sénégal" names Dakar, "Rue Dakaroise" does not. The API's rule
 * too (catalogue/return_points.names_town).
 */
export function namesTown(address, town) {
  const name = oneLine(town);
  if (!name) return false;
  return new RegExp(`(^|[^\\p{L}\\p{N}_])${escapeRegExp(name)}(?=$|[^\\p{L}\\p{N}_])`, "iu").test(oneLine(address));
}

/**
 * An address followed by its town, unless it already names it: a complete
 * address ("…, Diamniadio 12500, Sénégal") is not followed by "Diamniadio"
 * again. The API writes a branch the same way (return_points.branch_text).
 */
export function addressWithTown(address, town) {
  const street = oneLine(address);
  if (street && namesTown(street, town)) return street;
  return [street, oneLine(town)].filter(Boolean).join(", ");
}

/** An object with its place_id set, or removed when there is none; the same object when nothing changes. */
export function withPlaceId(target, placeId) {
  const current = target || {};
  if (placeId) return current.place_id === placeId ? current : { ...current, place_id: placeId };
  if (!("place_id" in current)) return current;
  const { place_id: _dropped, ...rest } = current;
  return rest;
}

/**
 * A form's location after a pick (or after the pick was typed over, place =
 * null): the place_id follows the pick, and the address parts the details
 * found fill only the fields that are still empty, so nothing a partner typed
 * is ever overwritten. `fields` maps a form key to a details key, e.g.
 * { city: "locality", postal_code: "postal_code", country: "country_code" }.
 * Latitude and longitude are never touched.
 */
export function fillFromPlace(location, place, fields = {}) {
  const next = withPlaceId(location, place?.place_id);
  if (!place) return next;
  let filled = next;
  for (const [formKey, detailsKey] of Object.entries(fields)) {
    const value = text(place[detailsKey]).trim();
    if (value && !text(filled[formKey]).trim()) filled = { ...filled, [formKey]: value };
  }
  return filled;
}
